import { ArrowLeftIcon } from "@phosphor-icons/react"
import { useEffect, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { Button, ButtonType } from "shared/components"

import { ProgressStep, ProgressSteps } from "components/ProgressSteps"
import { translateSchema } from "features/wizard/translateSchema.ts"

import publicWizardSchema from "./publicWizardSchema"
import ResultsCTA from "./ResultsCTA"
import * as schema from "./schema"
import WizardComponent, { WizardType } from "./Wizard"
import { WizardResult } from "./WizardResult"

enum Pages {
    Intro = "intro",
    Wizard = "wizard",
    Results = "results",
}

type PageWizardProgressStep = ProgressStep & {
    section: string
}

const createProgressSteps = (
    wizardSchema: schema.Schema,
): PageWizardProgressStep[] => {
    const steps: PageWizardProgressStep[] = [
        {
            title: "intro",
            completed: false,
            enabled: false,
            current: true,
            section: "intro",
        },
    ]

    // Add steps for each wizard question
    wizardSchema.questions.forEach((question) => {
        steps.push({
            title: question.id,
            completed: false,
            enabled: false,
            current: false,
            section: question.id, // Use the question ID as section identifier
        })
    })

    // Add results step
    steps.push({
        title: "results",
        completed: false,
        enabled: false,
        current: false,
        section: "results",
    })

    return steps
}

const exclusiveResults: string[] = [
    "result/article-2-12-open-source",
    "result/article-2-10-personal-usage",
    "result/article-5-prohibited",
    "result/article-2-excluded",
    "result/no-system-selected",
]

// hide these results when the child result is displayed
const dependentResults: { [key: string]: string[] } = {
    "result/article-25": [
        "result/article-5-prohibited",
        "result/article-2-excluded",
    ],
}

const roleResults: string[] = [
    "result/provider",
    "result/product-manufacturer",
    "result/deployer",
    "result/importer",
    "result/distributor",
    "result/authorized-representative",
]

const LEAD_EMAIL_STORAGE_KEY = "rw_lead_email"
const LEAD_CAPTURED_EVENT = "risk-wizard:lead-captured"
const COMPLETE_EVENT = "risk-wizard:complete"

function readLeadEmail(): string | null {
    try {
        return sessionStorage.getItem(LEAD_EMAIL_STORAGE_KEY)
    } catch {
        return null
    }
}

export default function PublicRiskPageWizard({
    color = "tertiary",
    translationPrefix,
}: {
    color?: "primary" | "tertiary"
    translationPrefix?: string
}) {
    const { t } = useTranslation()

    const isDebugMode = useMemo(() => {
        try {
            const urlParams = new URLSearchParams(window.location.search)
            return urlParams.get("debug") === "true"
        } catch {
            return false
        }
    }, [])

    const translatedSchema = useMemo(
        () => translateSchema(t, publicWizardSchema, translationPrefix),
        [t, translationPrefix],
    )

    const [leadEmail, setLeadEmail] = useState<string | null>(readLeadEmail)

    const [currentPage, setCurrentPage] = useState<Pages>(() =>
        readLeadEmail() ? Pages.Wizard : Pages.Intro,
    )
    const [results, setResults] = useState<schema.Result[]>([])

    useEffect(() => {
        const onLeadCaptured = (event: Event) => {
            const detail = (event as CustomEvent<{ email?: string }>).detail
            if (!detail?.email) return
            try {
                sessionStorage.setItem(LEAD_EMAIL_STORAGE_KEY, detail.email)
            } catch {
                // sessionStorage unavailable (private mode, etc.) — proceed in-memory
            }
            setLeadEmail(detail.email)
            setCurrentPage(Pages.Wizard)
        }
        window.addEventListener(LEAD_CAPTURED_EVENT, onLeadCaptured)
        return () => {
            window.removeEventListener(LEAD_CAPTURED_EVENT, onLeadCaptured)
        }
    }, [])

    const [progressSteps, setProgressSteps] = useState<
        PageWizardProgressStep[]
    >(() => createProgressSteps(translatedSchema))

    const [wizardSectionIndex, setWizardSectionIndex] = useState(0)

    useEffect(() => {
        if (currentPage === Pages.Wizard) {
            const targetStepIndex = wizardSectionIndex + 1

            setProgressSteps((prevSteps) => {
                if (
                    targetStepIndex >= 0 &&
                    targetStepIndex < prevSteps.length
                ) {
                    return prevSteps.map((step, index) => ({
                        ...step,
                        current: index === targetStepIndex,
                        completed: index < targetStepIndex,
                    }))
                }
                return prevSteps
            })
        }
    }, [wizardSectionIndex, currentPage])

    /* i18n-ignore:
        public_risk_wizard.steps.intro,
        public_risk_wizard.steps.developingOrProcuring,
        public_risk_wizard.steps.purpose,
        public_risk_wizard.steps.results
    */
    const translatedProgressSteps = useMemo(() => {
        return progressSteps.map((step) => {
            return {
                ...step,
                title: t(`wizard.public_risk_wizard.steps.${step.section}`),
            }
        })
    }, [progressSteps, t])

    const updateProgressSteps = (
        updates: Array<{
            index: number
            updates: Partial<Omit<PageWizardProgressStep, "title">>
        }>,
    ) => {
        setProgressSteps((prev) =>
            prev.map((step, i) => {
                const update = updates.find((u) => u.index === i)
                return update ? { ...step, ...update.updates } : step
            }),
        )
    }

    const handleComplete = () => {
        setCurrentPage(Pages.Results)
        const updates = progressSteps.map((_, index) => ({
            index,
            updates: {
                current: index === progressSteps.length - 1,
                completed: true,
            },
        }))
        updateProgressSteps(updates)
    }

    const handleNextSection = (_sectionTitle: string) => {
        setWizardSectionIndex((prev) => prev + 1)
    }

    const handlePreviousSection = (_sectionTitle: string) => {
        setWizardSectionIndex((prev) => Math.max(0, prev - 1))
    }

    const handleWizardStateChange = (
        sectionIndex: number,
        _questionIndex: number = 0,
    ) => {
        setWizardSectionIndex(sectionIndex)
    }

    const handleResults = (results_: schema.Result[]) => {
        setResults(results_)
    }

    useEffect(() => {
        if (currentPage !== Pages.Results || results.length === 0) return

        const serialisedResults = results.map((r) => ({
            id: String(r.id),
            title: r.title,
            category: r.info.category ?? "",
            description: r.info.description ?? "",
        }))
        const resultsSummary = serialisedResults
            .map((r) => `${r.category || "result"}: ${r.title}`)
            .join("\n")

        window.dispatchEvent(
            new CustomEvent(COMPLETE_EVENT, {
                detail: {
                    email: leadEmail,
                    results: serialisedResults,
                    resultsSummary,
                },
            }),
        )
    }, [currentPage, results, leadEmail])

    const IntroPage = () => (
        <div className="mx-auto flex min-h-120 max-w-2xl flex-col items-center justify-center gap-6 px-4 py-10">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-secondary,#ffe0ad)] text-3xl">
                ↑
            </div>
            <p className="text-text-secondary max-w-md text-center text-base sm:text-lg">
                {t("wizard.public_risk_wizard.email_gate.message")}
            </p>
        </div>
    )

    const ResultsPage = () => {
        const roleCategoryResults = results.filter(
            (result) => result.info.category === "role",
        )
        const riskCategoryResults = results.filter(
            (result) => result.info.category === "risk-level",
        )
        const obligationsCategoryResults = results.filter(
            (result) => result.info.category === "obligations",
        )

        const noCategoryResults =
            roleCategoryResults.length === 0 &&
            riskCategoryResults.length === 0 &&
            obligationsCategoryResults.length === 0

        const hasExclusiveResults = useMemo(
            () =>
                results.some((result) => exclusiveResults.includes(result.id)),
            [],
        )

        const hasDependentResults = useMemo(
            () =>
                Object.keys(dependentResults).some((parentId) => {
                    const parentExists = results.some(
                        (result) => result.id === parentId,
                    )
                    const childExists = results.some((result) =>
                        dependentResults[parentId].includes(result.id),
                    )
                    return parentExists && childExists
                }),
            [],
        )

        const resultsToDisplay = useMemo(() => {
            let filteredResults: schema.Result[] = results

            if (hasExclusiveResults) {
                filteredResults = filteredResults.filter(
                    (result) => !roleResults.includes(result.id),
                )
            }

            if (hasDependentResults) {
                const parentResultsToHide = Object.keys(
                    dependentResults,
                ).filter((parentId) => {
                    const parentExists = results.some(
                        (result) => result.id === parentId,
                    )
                    const childExists = results.some((result) =>
                        dependentResults[parentId].includes(result.id),
                    )
                    return parentExists && childExists
                })

                filteredResults = filteredResults.filter(
                    (result) => !parentResultsToHide.includes(result.id),
                )
            }

            const article25Result = filteredResults.findIndex(
                (result) => result.id === "result/article-25",
            )

            if (article25Result !== -1) {
                const article25 = filteredResults.splice(article25Result, 1)[0]
                filteredResults.splice(1, 0, article25)
            }

            return filteredResults
        }, [hasExclusiveResults, hasDependentResults])

        const CategoryResults = ({
            items,
            title = "",
        }: {
            items: schema.Result[]
            title?: string
        }) => {
            return (
                <>
                    {title && (
                        <h3 className="text-text-primary mb-2 text-lg font-medium sm:text-xl">
                            {title}
                        </h3>
                    )}
                    {items.map((item) => (
                        <WizardResult result={item} key={item.id} />
                    ))}
                </>
            )
        }

        return (
            <div className="mb-8 flex min-h-120 max-w-4xl flex-col gap-4 p-2 sm:mb-16 sm:p-4">
                <div className="flex justify-start">
                    <Button
                        buttonType={ButtonType.Secondary}
                        size="medium"
                        className="w-full sm:text-lg md:w-fit"
                        text={t("common.general.previous")}
                        iconLeft={ArrowLeftIcon}
                        enabled
                        onClick={() => setCurrentPage(Pages.Wizard)}
                    />
                </div>
                <h2 className="text-text-primary mt-2 text-left text-xl font-bold sm:mt-4 sm:text-2xl">
                    {t("wizard.public_risk_wizard.results.title")}
                </h2>
                <div className="flex w-full flex-col items-center justify-center gap-4">
                    {roleCategoryResults.length > 0 && !hasExclusiveResults && (
                        <CategoryResults
                            items={roleCategoryResults}
                            title={t("wizard.public_risk_wizard.results.role")}
                        />
                    )}
                    {riskCategoryResults.length > 0 && (
                        <CategoryResults
                            items={riskCategoryResults}
                            title={t("wizard.public_risk_wizard.results.risk")}
                        />
                    )}
                    {obligationsCategoryResults.length > 0 && (
                        <CategoryResults
                            items={obligationsCategoryResults}
                            title={t("wizard.public_risk_wizard.results.obligations")}
                        />
                    )}
                    {noCategoryResults && results.length > 0 && (
                        <CategoryResults items={resultsToDisplay} />
                    )}
                </div>

                <ResultsCTA
                    email={leadEmail}
                    results={resultsToDisplay.map((r) => ({
                        id: String(r.id),
                        title: r.title,
                        category: r.info.category ?? "",
                        description: r.info.description ?? "",
                    }))}
                    resultsSummary={resultsToDisplay
                        .map((r) => {
                            const desc = (r.info.description ?? "").trim()
                            return desc ? `${r.title}\n${desc}` : r.title
                        })
                        .join("\n\n")}
                />
            </div>
        )
    }

    return (
        <section className="mx-auto max-w-4xl">
            <ProgressSteps
                steps={translatedProgressSteps}
                type={color === "primary" ? "primary" : "tertiary"}
            />
            {currentPage === Pages.Intro && <IntroPage />}
            <div className={currentPage === Pages.Wizard ? "block" : "hidden"}>
                <WizardComponent
                    wizardSchema={translatedSchema}
                    onResults={handleResults}
                    onComplete={handleComplete}
                    onNextSection={handleNextSection}
                    onPreviousSection={handlePreviousSection}
                    onWizardStateChange={handleWizardStateChange}
                    type={WizardType.page}
                    color={color}
                    showSectionResults
                    debugMode={isDebugMode}
                />
            </div>
            {currentPage === Pages.Results && <ResultsPage />}
            <div className="text-text-secondary ml-2 text-left text-xs italic sm:ml-4 sm:text-sm">
                <Trans
                    i18nKey="wizard.public_risk_wizard.disclaimer"
                    components={{
                        mailto: (
                            <a
                                href="mailto:hello@addcomply.com"
                                className="text-link hover:text-link-hover cursor-pointer hover:underline"
                            />
                        ),
                    }}
                />
            </div>
        </section>
    )
}
